<template>
  <custom-dialog
    :is-dialog-open="isDialogOpen"
    dialog-title="Consultar disponibilidade"
    :max-width="dialogMaxWidth"
    :close-dialog="resetDataAndCloseDialog"
  >
    <v-form
      ref="formRef"
      class="multi-col-validation"
      @submit.prevent=""
    >
      <v-row>
        <v-col cols="12">
          <v-autocomplete
            v-model="agenteExternoSelecionado"
            dense
            outlined
            label="Agentes"
            item-text="nome"
            return-object
            no-data-text="Nenhum agente disponível"
            :items="agentesExternos"
            :loading="isGetAgentesExternosLoading"
            :rules="[validators.select]"
            @change="consultarDisponibilidade"
          />
        </v-col>
      </v-row>

      <v-row v-if="isConsultarDisponibilidadeLoading">
        <v-col
          cols="12"
          class="text-center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-col>
      </v-row>

      <v-expand-transition>
        <v-row v-if="events.length || isConsultarDisponibilidadeLoading">
          <v-col cols="12">
            <calendario-mensal
              :is-cadastro-agendamento="true"
              :events="events"
              :start-date="dataInicio"
              :end-date="dataFinal"
              @evento-selecionado="handleSelectEvento"
            />
          </v-col>
        </v-row>
      </v-expand-transition>
    </v-form>
  </custom-dialog>
</template>

<script>
import Vue from 'vue'
import { onMounted, ref, watch, computed } from '@vue/composition-api'
import { select } from '@/@core/utils/validation'
import CustomDialog from '@/components/dialog/Dialog.vue'
// DatePicker removido dos imports pois não é mais usado no template
import CalendarioMensal from '@/views/agenda/calendar/CalendarioMensal.vue'
import EquipeService from '@/services/modules/EquipeService'
import TicketService from '@/services/modules/TicketService'
import useDialog from '@/composables/useDialog'
import {
  getDataAtualNoFormatoIsoDateString,
  getTimeStringComApenasHorasEMinutos,
  converterDateTimeParaOFormatoIsoDateTimeString,
  converterLocaleDateStringParaOFormatoIsoDateString,
} from '@/utils/dateTime'
import {
  mapPeriodoDiaParaTitulo,
  passDateToDayAfter,
  replaceDateTimeWithNewTime,
} from '@/views/agenda/calendar/functions'

export default {
  emits: ['update:is-dialog-open', 'selecionar-data'],

  components: {
    CustomDialog,
    CalendarioMensal,
  },

  props: {
    isDialogOpen: {
      type: Boolean,
      required: true,
    },
    ticketId: {
      type: Number,
      required: true,
    },
  },

  setup(props, { emit }) {
    const { formRef, loading, resetFormValidation, closeDialog } = useDialog(emit, 'is-dialog-open')

    const agentesExternos = ref([])
    const cargoSelecionadoId = ref(false)
    const departamentoSelecionadoId = ref(false)
    const agenteExternoSelecionado = ref(null)
    const isGetAgentesExternosLoading = ref(false)
    const isConsultarDisponibilidadeLoading = ref(false)

    // Mantemos as variáveis de data para a API, mesmo sem os inputs visuais
    const dataInicio = ref(null)
    const dataFinal = ref(null)
    const dataAtual = ref(getDataAtualNoFormatoIsoDateString())

    const events = ref([])

    const dialogMaxWidth = computed(() => (events.value.length ? null : '1000'))

    async function getAgentesExternos() {
      if (isGetAgentesExternosLoading.value) return

      try {
        isGetAgentesExternosLoading.value = true
        agentesExternos.value = await EquipeService.getEquipesAgentesExternos(cargoSelecionadoId.value, departamentoSelecionadoId.value)
      } finally {
        isGetAgentesExternosLoading.value = false
      }
    }

    function setDataEHorarioIniciais() {
      const isoDateString = getDataAtualNoFormatoIsoDateString()
      const date = new Date(isoDateString)

      // Define o range padrão (ex: 1 mês ou 3 meses se preferir carregar mais histórico)
      const dateAcrescidaUmMes = new Date(date.getFullYear(), date.getMonth() + 1, 0).toLocaleDateString('pt-br')

      dataInicio.value = isoDateString
      dataFinal.value = converterLocaleDateStringParaOFormatoIsoDateString(dateAcrescidaUmMes)
    }

    function resetDataAndCloseDialog() {
      events.value = []
      cargoSelecionadoId.value = null
      departamentoSelecionadoId.value = null
      agenteExternoSelecionado.value = null
      setDataEHorarioIniciais()
      resetFormValidation()
      closeDialog()
    }

    function handleSelectEvento(evento) {
      const isoDateTimeString = converterDateTimeParaOFormatoIsoDateTimeString(evento.start)
      const [date, time] = isoDateTimeString.split('T')

      emit(
        'selecionar-data',
        {
          agenteId: agenteExternoSelecionado.value.id,
          agenteNome: agenteExternoSelecionado.value.nome,
          agenteEmail: agenteExternoSelecionado.value.email,
          agenteTelefone: agenteExternoSelecionado.value.telefone,
          periodoDiaId: evento.periodoDiaId,
          dataAgendamento: date,
          horarioVisitaAgendamento: getTimeStringComApenasHorasEMinutos(time),
        },
      )

      Vue.$toast.success('Período selecionado')
      resetDataAndCloseDialog()
    }

    // ALTERAÇÃO 2: Função renomeada e simplificada
    // É chamada automaticamente ao selecionar um agente
    async function consultarDisponibilidade() {
      // Se desmarcar o agente (limpar o campo), limpa o calendário
      if (!agenteExternoSelecionado.value) {
        events.value = []
        return
      }

      if (isConsultarDisponibilidadeLoading.value) return

      try {
        isConsultarDisponibilidadeLoading.value = true

        // Usa as datas padrão definidas no setDataEHorarioIniciais
        const result = await TicketService.getDisponibilidadesAgenteParaAgendamento(
          props.ticketId,
          agenteExternoSelecionado.value.id,
          dataInicio.value,
          dataFinal.value,
        )

        events.value = result
          .map(date => date.disponibilidadePorPeriodoDia
            .filter(it => it.disponivel)
            .map(disp => ({
              periodoDiaId: disp.periodoDiaId,
              name: mapPeriodoDiaParaTitulo(disp.periodoDiaId),
              start: replaceDateTimeWithNewTime(date.date, disp.horaInicial),
              end: disp.horaFinal > disp.horaInicial
                ? replaceDateTimeWithNewTime(date.date, disp.horaFinal)
                : passDateToDayAfter(replaceDateTimeWithNewTime(date.date, disp.horaFinal)),
              color: 'rgb(255, 255, 255)',
              timed: true,
            })))
          .flat()
      } finally {
        isConsultarDisponibilidadeLoading.value = false
      }
    }

    onMounted(() => {
      getAgentesExternos()
      setDataEHorarioIniciais()
    })

    watch([departamentoSelecionadoId, cargoSelecionadoId], getAgentesExternos)

    return {
      formRef,
      loading,
      closeDialog,
      agentesExternos,
      cargoSelecionadoId,
      departamentoSelecionadoId,
      agenteExternoSelecionado,
      isGetAgentesExternosLoading,
      isConsultarDisponibilidadeLoading,
      // Mantemos dataInicio/Fim para passar para o calendario, embora não editáveis
      dataInicio,
      dataFinal,
      dataAtual,
      events,
      dialogMaxWidth,
      resetDataAndCloseDialog,
      handleSelectEvento,
      consultarDisponibilidade, // Retornando a nova função
      validators: {
        select,
      },
    }
  },
}
</script>
